// ─── Copy Trade Positions Bus ────────────────────────────────
// Global pub/sub for cTrader position snapshots pushed by the
// shared SSE stream (server-driven reconcile). Decouples the
// stream from the copy-trade engine so the engine never opens its
// own cTrader WebSocket to read positions.
//
// The snapshot shape mirrors what `get-positions` used to return,
// so downstream parsing stays identical.

export interface PositionsSnapshot {
  accountId: number;
  reconcileRes: Record<string, unknown>;
  symbolRes: Record<string, unknown> | null;
  positionCount: number;
  timestamp: number;
}

type PositionsListener = (snapshot: PositionsSnapshot) => void;

class CopyTradePositionsBusImpl {
  private listeners: Set<PositionsListener> = new Set();
  private lastByAccount: Record<number, PositionsSnapshot> = {};

  /** Subscribe to positions snapshots for all accounts. Returns unsubscribe. */
  subscribe(fn: PositionsListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /** Publish a positions snapshot to all listeners. */
  publish(snapshot: PositionsSnapshot): void {
    this.lastByAccount[snapshot.accountId] = snapshot;
    this.listeners.forEach((fn) => {
      try {
        fn(snapshot);
      } catch {
        /* don't let one bad listener break others */
      }
    });
  }

  /** Get the last snapshot for an account (for late subscribers). */
  getLast(accountId: number): PositionsSnapshot | undefined {
    return this.lastByAccount[accountId];
  }

  /** Number of active listeners. */
  get listenerCount(): number {
    return this.listeners.size;
  }
}

export const copyTradePositionsBus = new CopyTradePositionsBusImpl();